import { NewReactModal } from '../NewReactModal';
import useComments from '../../hooks/useComments';
import CommentImages from '../CommentImages/CommentImages';
import { dateBR } from '../../utils/dateBR';
import './Comments.css';
import { useEffect } from 'react';

interface CommentsProps {
  productId: number;
  productTitle: string;
  productImage: string;
  headerModalTitle: string;
  modalId: string;
  siteKey: string;
  csrfToken: string;
  headerTitle?: string;
}

interface Comment {
  nome: string;
  rating: number;
  title: string;
  created: string;
  name: string;
  city: string;
  comment: string;
  images: any[];
}

function Comments(props: CommentsProps) {
  const {
    productId,
    productTitle,
    productImage,
    headerModalTitle,
    modalId,
    siteKey,
    csrfToken,
    headerTitle,
  } = props;

  const {
    getComments,
    comments,
    page,
    pages,
    total,
    ratings,
    commentsLoading,
  } = useComments();

  useEffect(() => {
    getComments(productId);
  }, [getComments, productId]);

  return (
    <div>
      {!commentsLoading ? (
        <>
          <Header headerTitle={headerTitle} />
          <div className="product-view-content" style={{ display: 'block' }}>
            {comments.length > 0 ? (
              <span>
                <ProductRating
                  productId={productId}
                  productTitle={productTitle}
                  productImage={productImage}
                  total={total}
                  ratings={Number(ratings.toFixed(2))}
                  headerModalTitle={headerModalTitle}
                  modalId={modalId}
                  siteKey={siteKey}
                  csrfToken={csrfToken}
                />
                <CommentList comments={comments} />
              </span>
            ) : (
              <EmptyComments
                headerModalTitle={headerModalTitle}
                modalId={modalId}
                siteKey={siteKey}
                csrfToken={csrfToken}
                productId={productId}
                productTitle={productTitle}
                productImage={productImage}
              />
            )}
            {page < pages ? (
              <div className="more-comments">
                <button
                  className="btn btn-primary"
                  onClick={() => getComments(productId, page + 1)}
                >
                  <span className="btn-text">Ver mais comentários</span>
                </button>
              </div>
            ) : null}
          </div>
        </>
      ) : (
        <div> Carregando comentários ...</div>
      )}
    </div>
  );
}

const Header = ({ headerTitle }: { headerTitle?: string }) => (
  <div className="header-box-content">
    <span className="title-secundary">
      <span className="icon-rating"></span>
      {headerTitle || 'Avaliação dos Clientes'}
    </span>
  </div>
);

const ProductRating = ({
  productTitle,
  productImage,
  total,
  ratings,
  headerModalTitle,
  modalId,
  siteKey,
  csrfToken,
  productId,
}: {
  productTitle: string;
  productImage: string;
  total: number;
  ratings: number;
  headerModalTitle: string;
  modalId: string;
  siteKey: string;
  csrfToken: string;
  productId: number;
}) => (
  <span>
    <div className="row">
      <p>Avaliações sobre o produto {productTitle}</p>
    </div>
    <div className="product-rating-row">
      <div
        itemProp="aggregateRating"
        itemType="http://schema.org/AggregateRating"
        itemScope
      >
        <meta itemProp="reviewCount" content={`${total}`} />
        <meta itemProp="ratingValue" content={`${ratings}`} />
      </div>
      <div className="product-rating">
        <div className="product-rating-stars-wrapper">
          <div
            className="product-rating-stars-bg"
            style={{ width: `${ratings * 20}%` }}
          ></div>
          <div className="product-rating-stars"></div>
        </div>
      </div>
      <div className="comments-counter">
        <div>
          <span>{ratings}</span> de <span>5,00</span> (Baseado em{' '}
          <span>{total}</span> avaliações)
        </div>
      </div>
      <div className="comments-button">
        <NewReactModal
          productId={productId}
          productTitle={productTitle}
          productImage={productImage}
          headerTitle={headerModalTitle}
          modalId={modalId}
          siteKey={siteKey}
          csrfToken={csrfToken}
        />
      </div>
    </div>
  </span>
);

const CommentList = ({ comments }: { comments: Comment[] }) => (
  <ul className="list-comments">
    {comments.map((comentario) => (
      <li
        key={comentario.created}
        className="list-comments-item"
        itemProp="review"
        itemType="http://schema.org/Review"
        itemScope
      >
        <meta itemProp="name" content={comentario.nome} />
        <div className="comment-info">
          <div className="comment-rating product-rating">
            <div className="product-rating-stars-wrapper">
              <div
                className="product-rating-stars-bg"
                style={{ width: `${comentario.rating * 20}%` }}
              ></div>
              <div className="product-rating-stars"></div>
            </div>
          </div>
          <div className="comment-header">
            <p className="comment-header-text">
              <meta itemProp="ratingValue" content={`${comentario.rating}`} />
              <meta itemProp="bestRating" content="5" />
              <span itemProp="reviewRating" itemScope itemType="http://schema.org/Rating">
                <span itemProp="ratingValue" content={`${comentario.rating}`}>{comentario.rating}</span>
              </span>
              <span>/5</span>
              {' - '}
              <span itemProp="author" itemScope itemType="https://schema.org/Person">
                <span itemProp="name" content={comentario.name}>{comentario.name}</span>
              </span>
              {' - '}
              <span>{comentario.city}</span> {' - '}
              <time itemProp="datePublished" dateTime={comentario.created}>
                {dateBR(comentario.created)}
              </time>
            </p>
          </div>
        </div>
        <div className="comment-info-customer"></div>
        <div className="comment-content">
          <div className="comment-descrition" itemProp="name">
            <strong>{comentario.title}</strong>
          </div>
          <div className="comment-descrition" itemProp="reviewBody">
            {comentario.comment}
          </div>
          {!!comentario.images && comentario?.images?.length > 0 ? (
            <CommentImages images={comentario.images} isPanel={false} />
          ) : null}
        </div>
      </li>
    ))}
  </ul>
);

const EmptyComments = ({
  headerModalTitle,
  modalId,
  siteKey,
  csrfToken,
  productId,
  productTitle,
  productImage,
}: {
  headerModalTitle: string;
  modalId: string;
  siteKey: string;
  csrfToken: string;
  productId: number;
  productTitle: string;
  productImage: string;
}) => (
  <ul className="list-comments">
    <li className="list-comments-item list-comments-item-empty">
      <div className="comment-text-empty">
        <p>Nenhum comentário foi postado para esse produto, seja o primeiro!</p>
      </div>
      <div className="comment-button">
        <NewReactModal
          productId={productId}
          productTitle={productTitle}
          productImage={productImage}
          headerTitle={headerModalTitle}
          modalId={modalId}
          siteKey={siteKey}
          csrfToken={csrfToken}
        />
      </div>
    </li>
  </ul>
);

export default Comments;
